const Alpha = window.alphaTheme || {};

const qs = (selector, root = document) => root.querySelector(selector);
const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
const debounce = (fn, wait = 180) => { let timer; return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), wait); }; };

function formatMoney(cents, format = Alpha.moneyFormat || '${{amount}}') {
  const value = Number(cents || 0);
  const amount = (value / 100).toFixed(2);
  const amountNoDecimals = Math.round(value / 100).toString();
  return format
    .replace(/{{\s*amount_no_decimals\s*}}/, amountNoDecimals)
    .replace(/{{\s*amount_with_comma_separator\s*}}/, amount.replace('.', ','))
    .replace(/{{\s*amount\s*}}/, amount);
}

function toast(message, type = 'success') {
  const stack = qs('[data-toast-stack]');
  if (!stack) return;
  const item = document.createElement('div');
  item.className = `toast toast--${type}`;
  item.textContent = message;
  stack.appendChild(item);
  setTimeout(() => item.remove(), 3600);
}

function lockBody(locked = true) { document.body.classList.toggle('is-locked', locked); }

function openDialog(dialog) {
  if (!dialog) return;
  if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
  lockBody(true);
}

function closeDialog(dialog) {
  if (!dialog) return;
  if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
  if (!qs('dialog[open]') && !qs('.cart-drawer.is-open') && !qs('.mobile-drawer.is-open')) lockBody(false);
}

function initDialogs(root = document) {
  qsa('[data-dialog-close]', root).forEach(btn => btn.addEventListener('click', () => closeDialog(btn.closest('dialog'))));
  qsa('dialog', root).forEach(dialog => dialog.addEventListener('click', event => { if (event.target === dialog) closeDialog(dialog); }));
  qsa('[data-open-search]', root).forEach(btn => btn.addEventListener('click', () => { const dialog = qs('[data-search-modal]'); openDialog(dialog); setTimeout(() => qs('[data-predictive-search-input]', dialog)?.focus(), 80); }));
}

function initLoader() {
  const loader = qs('[data-page-loader]');
  if (!loader) return;
  const hide = () => loader.classList.add('is-hidden');
  if (document.readyState === 'complete') hide(); else window.addEventListener('load', hide, { once: true });
  setTimeout(hide, 1600);
}

function initScrollProgress() {
  const line = qs('[data-scroll-progress]');
  if (!line) return;
  const update = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    line.style.width = `${max > 0 ? (window.scrollY / max) * 100 : 0}%`;
  };
  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
}

function initReveal(root = document) {
  const items = qsa('.reveal-item:not([data-reveal-bound])', root);
  if (!items.length) return;
  if (!Alpha.enableReveal || !('IntersectionObserver' in window)) { items.forEach(el => el.classList.add('is-revealed')); return; }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -30px' });
  items.forEach((el, index) => { el.dataset.revealBound = 'true'; el.style.transitionDelay = `${Math.min(index % 6, 5) * 65}ms`; observer.observe(el); });
}

function initParallax() {
  if (!Alpha.enableParallax || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const items = qsa('[data-parallax]');
  if (!items.length) return;
  const update = () => {
    items.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > innerHeight) return;
      const speed = Number(el.dataset.parallax || 0.08);
      el.style.transform = `translate3d(0, ${(rect.top - innerHeight / 2) * speed}px, 0)`;
    });
  };
  update(); window.addEventListener('scroll', update, { passive: true });
}

function initHeader() {
  const header = qs('[data-site-header]');
  if (!header) return;
  let lastY = window.scrollY;
  const update = () => {
    const current = window.scrollY;
    header.classList.toggle('is-sticky', current > 60);
    header.classList.toggle('is-hidden', current > 220 && current > lastY + 6 && !document.body.classList.contains('is-locked'));
    if (current < lastY) header.classList.remove('is-hidden');
    lastY = current;
  };
  update(); window.addEventListener('scroll', update, { passive: true });

  const drawer = qs('[data-mobile-drawer]');
  qsa('[data-mobile-menu-open]').forEach(btn => btn.addEventListener('click', () => { drawer?.classList.add('is-open'); lockBody(true); }));
  qsa('[data-mobile-menu-close]').forEach(btn => btn.addEventListener('click', () => { drawer?.classList.remove('is-open'); lockBody(false); }));
}

async function refreshCartDrawer(open = false) {
  const drawer = qs('[data-cart-drawer]');
  if (!drawer) return;
  try {
    const separator = Alpha.routes.cart.includes('?') ? '&' : '?';
    const response = await fetch(`${Alpha.routes.cart}${separator}section_id=cart-drawer`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const html = await response.text();
    const holder = document.createElement('div'); holder.innerHTML = html;
    const fresh = qs('[data-cart-drawer]', holder);
    if (fresh) drawer.replaceWith(fresh);
    initCart(qs('[data-cart-drawer]'));
    if (open) openCartDrawer();
  } catch (error) { console.warn(error); }
  updateCartCount();
}

async function updateCartCount() {
  try {
    const cart = await fetch(`${Alpha.routes.root}cart.js`).then(r => r.json());
    qsa('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
  } catch (error) { console.warn(error); }
}

function openCartDrawer() {
  const drawer = qs('[data-cart-drawer]');
  if (!drawer) { window.location.href = Alpha.routes.cart; return; }
  drawer.classList.add('is-open'); lockBody(true);
}
function closeCartDrawer() { qs('[data-cart-drawer]')?.classList.remove('is-open'); lockBody(false); }

async function ajaxAdd(form) {
  const button = qs('[type="submit"]', form);
  const original = button?.textContent;
  if (button) { button.disabled = true; button.textContent = 'Adding…'; }
  try {
    const response = await fetch(Alpha.routes.cartAdd, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: new FormData(form) });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Could not add this item.');
    toast(`${data.product_title || 'Item'} added to your ritual bag.`);
    await refreshCartDrawer(Alpha.cartDrawer);
    document.dispatchEvent(new CustomEvent('shopify:cart:lines-update', { detail: { source: 'alpha-theme' } }));
  } catch (error) { toast(error.message, 'error'); }
  finally { if (button) { button.disabled = false; button.textContent = original; } }
}

async function changeCartLine(key, quantity) {
  try {
    const response = await fetch(Alpha.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    if (!response.ok) throw new Error('Unable to update cart.');
    await refreshCartDrawer(false);
  } catch (error) { toast(error.message, 'error'); }
}

function initCart(root = document) {
  if (!root) return;
  qsa('[data-cart-open]', root).forEach(btn => btn.addEventListener('click', event => { if (Alpha.cartDrawer) { event.preventDefault(); openCartDrawer(); } }));
  qsa('[data-cart-close]', root).forEach(btn => btn.addEventListener('click', closeCartDrawer));
  qsa('[data-ajax-cart-form]', root).forEach(form => { if (form.dataset.bound) return; form.dataset.bound = 'true'; form.addEventListener('submit', event => { event.preventDefault(); ajaxAdd(form); }); });
  qsa('[data-cart-quantity]', root).forEach(input => {
    if (input.dataset.bound) return; input.dataset.bound = 'true';
    input.addEventListener('change', () => changeCartLine(input.dataset.key, Math.max(0, Number(input.value))));
  });
  qsa('[data-quantity-button]', root).forEach(btn => {
    if (btn.dataset.bound) return; btn.dataset.bound = 'true';
    btn.addEventListener('click', () => {
      const input = qs('input', btn.closest('[data-quantity-control]'));
      if (!input) return;
      const next = Math.max(Number(input.min || 0), Number(input.value) + Number(btn.dataset.quantityButton));
      input.value = next; input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
  qsa('[data-cart-remove]', root).forEach(btn => btn.addEventListener('click', event => { event.preventDefault(); changeCartLine(btn.dataset.key, 0); }));
  const note = qs('[data-cart-note]', root);
  note?.addEventListener('change', debounce(async () => {
    await fetch(Alpha.routes.cartUpdate, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: note.value }) });
    toast('Order note saved.');
  }, 350));
}

function initPredictiveSearch() {
  const form = qs('[data-predictive-search-form]');
  const input = qs('[data-predictive-search-input]', form);
  const results = qs('[data-predictive-search-results]', form);
  if (!form || !input || !results || !Alpha.predictiveSearch) return;
  const search = debounce(async () => {
    const term = input.value.trim();
    if (term.length < 2) { results.innerHTML = ''; return; }
    results.innerHTML = '<div class="predictive-search__loading">Searching…</div>';
    try {
      const params = new URLSearchParams({ q: term, 'resources[type]': 'product,collection,page,article,query', 'resources[limit]': '6', section_id: 'predictive-search' });
      const response = await fetch(`${Alpha.routes.predictiveSearch}?${params}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      results.innerHTML = qs('#shopify-section-predictive-search', doc)?.innerHTML || text;
    } catch (error) { results.innerHTML = '<p>Search is temporarily unavailable.</p>'; }
  }, 220);
  input.addEventListener('input', search);
}

const storage = {
  get(key) { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } },
  set(key, value) { localStorage.setItem(key, JSON.stringify(value)); }
};
function toggleStoredList(key, handle) {
  let list = storage.get(key);
  const exists = list.includes(handle);
  list = exists ? list.filter(item => item !== handle) : [handle, ...list].slice(0, 30);
  storage.set(key, list); return !exists;
}
function updateStoredStates() {
  const wishlist = storage.get('alpha_wishlist');
  const compare = storage.get('alpha_compare');
  qsa('[data-wishlist-toggle]').forEach(btn => btn.classList.toggle('is-active', wishlist.includes(btn.dataset.handle)));
  qsa('[data-compare-toggle]').forEach(btn => btn.classList.toggle('is-active', compare.includes(btn.dataset.handle)));
  qsa('[data-wishlist-count]').forEach(el => { el.textContent = wishlist.length; el.hidden = wishlist.length === 0; });
  qsa('[data-compare-count]').forEach(el => { el.textContent = compare.length; el.hidden = compare.length === 0; });
}
function initStoredLists(root = document) {
  qsa('[data-wishlist-toggle]', root).forEach(btn => { if (btn.dataset.bound) return; btn.dataset.bound = 'true'; btn.addEventListener('click', () => { const added = toggleStoredList('alpha_wishlist', btn.dataset.handle); updateStoredStates(); toast(added ? 'Added to wishlist.' : 'Removed from wishlist.'); }); });
  qsa('[data-compare-toggle]', root).forEach(btn => { if (btn.dataset.bound) return; btn.dataset.bound = 'true'; btn.addEventListener('click', () => { let list = storage.get('alpha_compare'); if (!list.includes(btn.dataset.handle) && list.length >= 4) { toast('Compare up to four products.', 'error'); return; } const added = toggleStoredList('alpha_compare', btn.dataset.handle); updateStoredStates(); toast(added ? 'Added to comparison.' : 'Removed from comparison.'); }); });
  updateStoredStates();
}

async function productData(handle) {
  const response = await fetch(`${Alpha.routes.root}products/${handle}.js`);
  if (!response.ok) throw new Error('Product unavailable');
  return response.json();
}
function renderLocalProduct(product, mode = 'wishlist') {
  const image = product.featured_image || product.images?.[0] || '';
  const price = formatMoney(product.price);
  if (mode === 'compare') return `<article class="compare-item" data-handle="${product.handle}"><img src="${image}" alt="${product.title.replace(/"/g,'&quot;')}" loading="lazy"><h3><a href="${product.url}">${product.title}</a></h3><strong>${price}</strong><dl><dt>Type</dt><dd>${product.type || 'Grooming essential'}</dd><dt>Vendor</dt><dd>${product.vendor || 'Alpha Men’s'}</dd><dt>Variants</dt><dd>${product.variants?.length || 1}</dd></dl><button class="button button--secondary button--full" data-remove-local="alpha_compare" data-handle="${product.handle}">Remove</button></article>`;
  return `<article class="product-card"><div class="product-card__media product-card__media--portrait"><a href="${product.url}" class="product-card__image-link"><img class="product-card__image product-card__image--primary" src="${image}" alt="${product.title.replace(/"/g,'&quot;')}" loading="lazy"></a></div><div class="product-card__content"><div class="product-card__eyebrow">${product.type || 'Alpha essential'}</div><h3 class="product-card__title"><a href="${product.url}">${product.title}</a></h3><div class="product-card__bottom"><div class="price"><span>${price}</span></div></div><button class="button button--secondary button--full" data-remove-local="alpha_wishlist" data-handle="${product.handle}">Remove</button></div></article>`;
}
async function hydrateLocalGrid(container, key, mode) {
  if (!container) return;
  const handles = storage.get(key);
  if (!handles.length) { container.innerHTML = `<div class="empty-state"><div class="empty-state__icon">♡</div><h2>Nothing saved yet.</h2><p>Use the heart or compare controls on product cards.</p><a class="button button--primary" href="${Alpha.routes.root}collections/all">Explore products</a></div>`; return; }
  container.innerHTML = '<p>Loading your saved products…</p>';
  const products = (await Promise.all(handles.map(handle => productData(handle).catch(() => null)))).filter(Boolean);
  container.innerHTML = products.map(product => renderLocalProduct(product, mode)).join('');
  qsa('[data-remove-local]', container).forEach(btn => btn.addEventListener('click', () => { const list = storage.get(btn.dataset.removeLocal).filter(h => h !== btn.dataset.handle); storage.set(btn.dataset.removeLocal, list); hydrateLocalGrid(container, btn.dataset.removeLocal, mode); updateStoredStates(); }));
}
function initLocalPages() {
  hydrateLocalGrid(qs('[data-wishlist-grid]'), 'alpha_wishlist', 'wishlist');
  hydrateLocalGrid(qs('[data-compare-grid]'), 'alpha_compare', 'compare');
}

function rememberProduct() {
  const el = qs('[data-current-product-handle]');
  if (!el) return;
  const handle = el.dataset.currentProductHandle;
  let list = storage.get('alpha_recent');
  list = [handle, ...list.filter(h => h !== handle)].slice(0, 8);
  storage.set('alpha_recent', list);
}
async function hydrateRecentlyViewed() {
  const grid = qs('[data-recently-viewed-grid]');
  if (!grid) return;
  const current = qs('[data-current-product-handle]')?.dataset.currentProductHandle;
  const handles = storage.get('alpha_recent').filter(h => h !== current).slice(0, Number(grid.dataset.limit || 4));
  if (!handles.length) { grid.closest('section')?.classList.add('hide'); return; }
  const products = (await Promise.all(handles.map(h => productData(h).catch(() => null)))).filter(Boolean);
  grid.innerHTML = products.map(product => renderLocalProduct(product, 'wishlist')).join('');
}

async function openQuickView(url) {
  const dialog = qs('[data-quick-view-modal]');
  const content = qs('[data-quick-view-content]', dialog);
  if (!dialog || !content) return;
  content.innerHTML = '<div style="padding:4rem;text-align:center">Loading…</div>'; openDialog(dialog);
  try {
    const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    content.innerHTML = qs('#shopify-section-quick-view', doc)?.innerHTML || html;
    initCart(content); initProductForms(content);
  } catch { content.innerHTML = '<div class="empty-state"><h2>Unable to load product.</h2></div>'; }
}
function initQuickView(root = document) { qsa('[data-quick-view]', root).forEach(btn => { if (btn.dataset.bound) return; btn.dataset.bound = 'true'; btn.addEventListener('click', () => openQuickView(btn.dataset.quickView)); }); }

function findVariant(variants, options) { return variants.find(variant => variant.options.every((value, index) => value === options[index])); }
function initProductForms(root = document) {
  qsa('[data-product-root]', root).forEach(productRoot => {
    if (productRoot.dataset.bound) return; productRoot.dataset.bound = 'true';
    const dataScript = qs('[data-product-json]', productRoot);
    if (!dataScript) return;
    let variants = []; try { variants = JSON.parse(dataScript.textContent); } catch { return; }
    const form = qs('form[action*="/cart/add"]', productRoot);
    const idInput = qs('[name="id"]', form);
    const addButton = qs('[data-add-to-cart]', productRoot);
    const price = qs('[data-product-price]', productRoot);
    const compare = qs('.price__compare', productRoot);
    const getOptions = () => qsa('[data-option-position]', productRoot).sort((a,b) => Number(a.dataset.optionPosition)-Number(b.dataset.optionPosition)).map(group => qs('input:checked,select', group)?.value);
    const update = () => {
      const variant = findVariant(variants, getOptions());
      if (!variant) { if (addButton) { addButton.disabled = true; addButton.textContent = 'Unavailable'; } return; }
      if (idInput) idInput.value = variant.id;
      if (price) price.textContent = formatMoney(variant.price);
      if (compare) { compare.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : ''; compare.hidden = !(variant.compare_at_price > variant.price); }
      if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? addButton.dataset.availableText || 'Add to cart' : addButton.dataset.soldText || 'Sold out'; }
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      qsa('[data-sticky-variant-id]', productRoot).forEach(el => el.value = variant.id);
      qsa('[data-sticky-price]', productRoot).forEach(el => el.textContent = formatMoney(variant.price));
    };
    qsa('[data-option-position] input,[data-option-position] select', productRoot).forEach(input => input.addEventListener('change', update));
    qsa('[data-gallery-thumb]', productRoot).forEach(btn => btn.addEventListener('click', () => { const id = btn.dataset.galleryThumb; qs(`#${CSS.escape(id)}`, productRoot)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); qsa('[data-gallery-thumb]', productRoot).forEach(x => x.classList.toggle('is-active', x === btn)); }));
    update();
  });
}

function initBeforeAfter(root = document) {
  qsa('[data-before-after]', root).forEach(wrapper => {
    const input = qs('input[type="range"]', wrapper);
    input?.addEventListener('input', () => wrapper.style.setProperty('--position', `${input.value}%`));
  });
}

function initCountdowns(root = document) {
  qsa('[data-countdown]', root).forEach(countdown => {
    const target = new Date(countdown.dataset.countdown).getTime();
    if (!target) return;
    const update = () => {
      const distance = Math.max(0, target - Date.now());
      const values = { days: Math.floor(distance / 86400000), hours: Math.floor(distance / 3600000) % 24, minutes: Math.floor(distance / 60000) % 60, seconds: Math.floor(distance / 1000) % 60 };
      Object.entries(values).forEach(([key, value]) => { const el = qs(`[data-${key}]`, countdown); if (el) el.textContent = String(value).padStart(2, '0'); });
      if (distance === 0) clearInterval(timer);
    };
    update(); const timer = setInterval(update, 1000);
  });
}

function initCounters(root = document) {
  const counters = qsa('[data-counter]', root);
  if (!counters.length) return;
  const observer = new IntersectionObserver(entries => entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    const el = entry.target; const target = Number(el.dataset.counter || 0); const suffix = el.dataset.suffix || ''; const start = performance.now();
    const tick = now => { const progress = Math.min(1, (now - start) / 1100); el.textContent = `${Math.round(target * (1 - Math.pow(1-progress,3)))}${suffix}`; if (progress < 1) requestAnimationFrame(tick); };
    requestAnimationFrame(tick); observer.unobserve(el);
  }), { threshold: .5 });
  counters.forEach(el => observer.observe(el));
}

function initLocalization(root = document) { qsa('[data-localization-select]', root).forEach(select => select.addEventListener('change', () => select.form.submit())); }

function initPopup() {
  const popup = qs('[data-premium-popup]');
  if (!popup || sessionStorage.getItem(`alpha_popup_${popup.dataset.popupId}`)) return;
  const delay = Number(popup.dataset.delay || 7) * 1000;
  const show = () => { openDialog(popup); sessionStorage.setItem(`alpha_popup_${popup.dataset.popupId}`, '1'); };
  if (popup.dataset.trigger === 'exit') {
    const handler = event => { if (event.clientY <= 5) { show(); document.removeEventListener('mouseout', handler); } };
    document.addEventListener('mouseout', handler);
  } else setTimeout(show, delay);
}

function initAll(root = document) {
  initDialogs(root); initReveal(root); initCart(root); initStoredLists(root); initQuickView(root); initProductForms(root); initBeforeAfter(root); initCountdowns(root); initCounters(root); initLocalization(root);
}

document.addEventListener('DOMContentLoaded', () => {
  initLoader(); initScrollProgress(); initHeader(); initParallax(); initPredictiveSearch(); initAll(); initLocalPages(); rememberProduct(); hydrateRecentlyViewed(); initPopup(); updateCartCount();
});

document.addEventListener('shopify:section:load', event => initAll(event.target));
document.addEventListener('shopify:section:unload', () => closeCartDrawer());
